import { projectLibrary } from '@/adapter/libraryProjection';
import BlockApi from '@/api/api';
import LoadApplicationDialog from '@/components/navigation/wizard/load/LoadApplicationDialog';
import { useApplication } from '@/hooks/useApplication';
import { useConfig } from '@/hooks/useConfig';
import { useDialogs } from '@/hooks/useDialogs';
import { useMetadata } from '@/hooks/useMetadata';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { BackendApplication, ReleaseTarget } from '@/types/backend';
import { migrateApplication } from '@/util/migration/migration';
import { isApplication, isNumber } from '@/util/typeGuards';
import { FC } from 'react';
import { useAuth } from 'react-oidc-context';

type AddLibraryDialogProps = {
  open: boolean;
  onClose: () => void;
};

const AddLibraryDialog: FC<AddLibraryDialogProps> = (props) => {
  const auth = useAuth();
  const { config } = useConfig();
  const { metadata } = useMetadata();
  const { application, setApplication } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();
  const { showError, showProgress, closeProgress, showTable } = useDialogs();

  const addLibrary = async (backendApplication: BackendApplication) => {
    if (!auth.user?.access_token) return;

    const { applicationId, name } = backendApplication;
    const shownName = backendApplication.label || name;

    if (!applicationId || !name) {
      showError({ title: 'Could not add the library', message: 'The selected application carries no id or name.' });
      return;
    }

    if (application.libraryReferences?.some((reference) => reference.name === name)) {
      showError({ title: 'Could not add the library', message: `${shownName} is already added as a library.` });
      return;
    }

    showProgress('Adding Library...');
    try {
      const api = BlockApi(config.apiUrl, auth.user.access_token);

      const releases = await api.release.current(ReleaseTarget.All, applicationId);
      const revision = releases[0]?.revision;
      if (!isNumber(revision)) {
        throw new Error(`${shownName} is not published. Publish it to All before adding it as a library.`);
      }

      const loaded = await api.application.load(applicationId, revision);
      const parsed = loaded?.blockData ? JSON.parse(loaded.blockData) : undefined;
      if (!isApplication(parsed)) {
        throw new Error(`The published revision ${revision} of ${shownName} is not a valid application.`);
      }
      const library = migrateApplication(parsed, metadata, showTable);
      setApplication(projectLibrary(application, library, name));
      setUpdateNodes(['*']);
    } catch (error) {
      showError({
        title: 'Could not add the library',
        message: error instanceof Error ? error.message : 'An error occurred while loading the library.',
        stacktrace: error
      });
    }
    closeProgress();
  };

  return (
    <LoadApplicationDialog
      open={props.open}
      onClose={props.onClose}
      title='Add Library'
      confirmLabel='Add'
      loadApplication={addLibrary}
    />
  );
};

export default AddLibraryDialog;
